import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Film, Image as ImageIcon, Download } from "lucide-react";
import { S, MANROPE, FOCUS, API } from "./lib";

const KB = 1024;
const prettySize = (n) => (n > KB * KB ? `${(n / KB / KB).toFixed(1)}MB` : `${Math.max(1, Math.round(n / KB))}KB`);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/* Say what actually went wrong. A failed upload is either the server refusing it
   (it tells us why), the session having expired while the tab sat open, or the
   connection dropping — and the fix is different for each. */
const explain = (e, filename) => {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (!e?.response) {
    return e?.code === "ECONNABORTED"
      ? `${filename} timed out on the way up — the connection stalled. Try again.`
      : `The connection dropped while sending ${filename}. Check your network and try again.`;
  }
  if (status === 401) return "Your session expired while this tab was open — log in again, then re-upload.";
  if (status === 403) return "You do not have permission to add files to this post.";
  if (status === 413) return `${filename} was rejected as too large by the server (${status}).`;
  if (typeof detail === "string" && detail) return detail;
  return `Upload of ${filename} failed — the server returned HTTP ${status}.`;
};

/* Retry only the failures that a retry can fix: a dropped connection, a 429, or
   a 5xx. A 400/401/413 will fail again identically, so surface it immediately. */
const worthRetrying = (e) => {
  const status = e?.response?.status;
  if (!e?.response) return true;
  return status === 429 || status >= 500;
};

const withRetry = async (fn, onAttempt) => {
  let last;
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (e) {
      last = e;
      if (attempt === 3 || !worthRetrying(e)) throw e;
      onAttempt?.(attempt);
      // eslint-disable-next-line no-await-in-loop
      await sleep(attempt * 1500);
    }
  }
  throw last;
};

const Thumb = ({ postId, asset }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (asset.kind !== "image") return undefined;
    let url;
    let live = true;
    axios.get(`${API}/posts/${postId}/assets/${asset.id}`, { responseType: "blob" })
      .then((r) => { if (!live) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [postId, asset.id, asset.kind]);

  if (asset.kind === "image" && src) {
    return <img src={src} alt={asset.filename} data-testid={`social-asset-thumb-${asset.id}`}
      className="w-full h-full object-cover" />;
  }
  return (
    <div className="w-full h-full flex items-center justify-center" style={{ background: S.panel }}>
      {asset.kind === "video" ? <Film className="w-5 h-5" style={{ color: S.ink3 }} />
        : <ImageIcon className="w-5 h-5" style={{ color: S.muted }} />}
    </div>
  );
};

export default function SocialAssets({ post, onChanged }) {
  const [assets, setAssets] = useState(post.assets || []);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { setAssets(post.assets || []); }, [post.assets]);

  const open = async (asset) => {
    try {
      const r = await axios.get(`${API}/posts/${post.id}/assets/${asset.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not open that file"); }
  };

  const remove = async (asset) => {
    try {
      await axios.delete(`${API}/posts/${post.id}/assets/${asset.id}`);
      setAssets((a) => a.filter((x) => x.id !== asset.id));
      toast.success("File removed");
      onChanged?.();
    } catch { toast.error("Could not remove that file"); }
  };

  const chunkedUpload = useCallback(async (file) => {
    const init = await axios.post(`${API}/posts/${post.id}/assets/init`, {
      filename: file.name, content_type: file.type, size: file.size,
    });
    const { upload_id, chunk_size } = init.data;
    const total = Math.ceil(file.size / chunk_size);
    for (let i = 0; i < total; i += 1) {
      const sendChunk = () => {
        const body = new FormData();
        body.append("upload_id", upload_id);
        body.append("index", String(i));
        body.append("file", file.slice(i * chunk_size, (i + 1) * chunk_size), `${file.name}.part${i}`);
        return axios.post(`${API}/assets/chunk`, body, { timeout: 120000 });
      };
      // eslint-disable-next-line no-await-in-loop
      await withRetry(sendChunk, (n) => setProgress({
        name: file.name, pct: Math.round((i / total) * 95), retry: n }));
      setProgress({ name: file.name, pct: Math.round(((i + 1) / total) * 95) });
    }
    const done = new FormData();
    done.append("upload_id", upload_id);
    done.append("chunks", String(total));
    const r = await withRetry(() => axios.post(`${API}/assets/complete`, done, { timeout: 120000 }));
    return r.data;
  }, [post.id]);

  const oneShotUpload = useCallback((file) => withRetry(
    () => {
      const body = new FormData();
      body.append("file", file);
      return axios.post(`${API}/posts/${post.id}/assets`, body, {
        timeout: 120000,
        onUploadProgress: (e) => setProgress({
          name: file.name, pct: e.total ? Math.round((e.loaded / e.total) * 95) : 50 }),
      });
    },
    (n) => setProgress({ name: file.name, pct: 2, retry: n }),
  ).then((r) => r.data), [post.id]);

  const send = async (files) => {
    for (const file of files) {
      setProgress({ name: file.name, pct: 2 });
      try {
        // eslint-disable-next-line no-await-in-loop
        const result = file.size > 4 * KB * KB ? await chunkedUpload(file) : await oneShotUpload(file);
        setAssets(result.post?.assets || []);
        toast.success(`${file.name} is on the post`);
        onChanged?.(result.post);
      } catch (e) {
        toast.error(explain(e, file.name));
      } finally {
        setProgress(null);
      }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="rounded-[10px] p-3.5 space-y-3" style={{ background: S.card, border: `1px solid ${S.border}` }}
      data-testid="social-assets">
      <div className="flex items-center justify-between">
        <div style={{ ...MANROPE, fontSize: 13.5, fontWeight: 700, color: S.ink }}>
          Artwork and reel
          {assets.length > 0 && <span className="text-[11.5px] font-normal ml-1.5" style={{ color: S.ink3 }}>
            · {assets.length} file{assets.length > 1 ? "s" : ""}</span>}
        </div>
        <label className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase cursor-pointer ${FOCUS}`}
          style={{ color: S.accentInk, letterSpacing: "0.04em" }} data-testid="social-asset-pick">
          <Upload className="w-3 h-3" /> Add files
          <input ref={fileRef} type="file" multiple className="hidden" data-testid="social-asset-input"
            accept="image/*,video/mp4,video/quicktime,video/webm"
            onChange={(e) => send(Array.from(e.target.files || []))} />
        </label>
      </div>

      {progress && (
        <div data-testid="social-asset-progress">
          <div className="flex items-center gap-1.5 text-[11.5px] mb-1" style={{ color: S.ink2 }}>
            <Loader2 className="w-3 h-3 animate-spin" /> {progress.name} · {progress.pct}%
            {progress.retry && <span style={{ color: "#8E1212" }}>
              · connection dropped, retrying ({progress.retry} of 3)</span>}
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: S.divider }}>
            <div className="h-full" style={{ width: `${progress.pct}%`, background: S.accentEdge }} />
          </div>
        </div>
      )}

      {assets.length === 0 && !progress ? (
        <p className="text-[12px]" style={{ color: S.ink3 }}>
          Drop the finished 1080×1080 card and the 9:16 reel here and the whole pack lives with the brief.
          Big videos go up in pieces, so a 100MB reel is fine.
        </p>
      ) : (
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))" }}>
          {assets.map((a) => (
            <div key={a.id} className="rounded-[8px] overflow-hidden" data-testid={`social-asset-${a.id}`}
              style={{ border: `1px solid ${S.border}`, background: S.card }}>
              <button onClick={() => open(a)} className={`block w-full h-[86px] ${FOCUS}`}
                data-testid={`social-asset-open-${a.id}`} title="Open">
                <Thumb postId={post.id} asset={a} />
              </button>
              <div className="px-1.5 py-1.5">
                <div className="text-[11px] truncate" style={{ color: S.ink }} title={a.filename}>{a.filename}</div>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="mono text-[10px]" style={{ color: S.muted }}>{prettySize(a.size || 0)}</span>
                  <span className="flex items-center gap-1">
                    <button onClick={() => open(a)} title="Open" className={FOCUS}
                      data-testid={`social-asset-download-${a.id}`}>
                      <Download className="w-3 h-3" style={{ color: S.ink3 }} />
                    </button>
                    <button onClick={() => remove(a)} title="Remove" className={FOCUS}
                      data-testid={`social-asset-delete-${a.id}`}>
                      <Trash2 className="w-3 h-3" style={{ color: "#8E1212" }} />
                    </button>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
